import axios, {
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'

import { refresh } from './auth'
import {
  clearStorage,
  getAccessToken,
  getDeviceId,
  getRefreshToken,
} from './client-storage'
import { useAppStore } from '@/stores'

const clearAuthenticationState = () =>
  useAppStore.getState().clearAuthenticationState()

/** What every request is given, unless it names a longer one. */
const DEFAULT_TIMEOUT_MS = 30_000

/** What a request carrying a file is given, so a 50 MB upload on a slow
 *  connection is not cancelled under it. */
const UPLOAD_TIMEOUT_MS = 5 * 60_000

/**
 * For a call that waits on something slower than a database — a headless
 * Chromium render, a model.
 *
 * A request that outruns the default is cancelled here while the server keeps
 * working, so its write lands with nobody watching. Passed per call rather
 * than raised for everything: thirty seconds is the right answer for a request
 * that is only reading rows, and a screen that hangs on one of those has a
 * problem worth seeing.
 */
export const SLOW_REQUEST_TIMEOUT_MS = 3 * 60_000

/**
 * No `Content-Type`: axios derives it from the body, and one fixed here is
 * wrong for every body that is not JSON. Declare `application/json` and axios
 * serialises a `FormData` with `JSON.stringify` — the file never reaches the
 * wire, and the multipart parser on the other side finds nothing to read.
 */
const config = {
  baseURL: import.meta.env.VITE_API_URL,
  timeout: DEFAULT_TIMEOUT_MS,
}

// Axios instance to use for all public requests
export const publicClient = axios.create(config)

// Main Axios instance to use for all secured requests
export const apiClient = axios.create(config)

/** A `FormData` body is the only one that carries a file, and the only one
 *  worth minutes. Read off the body, so there is nothing for a call site to
 *  announce. */
const raiseTimeoutForUploads = (request: InternalAxiosRequestConfig) => {
  if (request.data instanceof FormData) request.timeout = UPLOAD_TIMEOUT_MS
  return request
}
publicClient.interceptors.request.use(raiseTimeoutForUploads)
apiClient.interceptors.request.use(raiseTimeoutForUploads)

// Nest sends `null` and `undefined` as an empty body, which axios hands back
// as `''`. An empty string is not nullish, so `?? fallback` chains at the call
// sites would keep it and treat "no result" as a result.
const emptyBodyToNull = (response: AxiosResponse): AxiosResponse => {
  if (response.data === '') response.data = null
  return response
}
publicClient.interceptors.response.use(emptyBodyToNull)
apiClient.interceptors.response.use(emptyBodyToNull)

// Attach Authorization header before sending requests (insta reject if no accessToken is available)
apiClient.interceptors.request.use(config => {
  const accessToken = getAccessToken()

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`

    return config
  }

  return Promise.reject({
    config,
    response: {
      status: 401,
      data: { message: 'Unauthorized', statusCode: 401 },
    },
  })
})

// Global variable to track the refresh request
let refreshPromise: Promise<string> | null = null

// Refresh token logic
apiClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config
    const refreshToken = getRefreshToken()
    const deviceId = getDeviceId()

    // If unauthorized (401), tokens exist and request hasn't been retried yet
    if (
      error.response?.status === 401 &&
      refreshToken &&
      deviceId &&
      !originalRequest?._triedToRefresh
    ) {
      if (originalRequest) originalRequest._triedToRefresh = true

      // If there is no refresh in progress yet, register one
      if (!refreshPromise) {
        refreshPromise = refresh({ refreshToken, deviceId })
      }

      try {
        // Wait for refresh to finish
        await refreshPromise

        return apiClient(originalRequest)
      } catch {
        clearAuthenticationState()
        clearStorage()
      } finally {
        // Cleared on the way out either way: a promise left behind rejected
        // makes every later 401 re-throw the old failure instead of trying a
        // refresh — including after signing in again in the same tab.
        refreshPromise = null
      }
    }

    // Return the error if not 401 or already retried
    return Promise.reject(error)
  },
)
